import { ProviderMark } from "../../../auth/components/ProviderButton";
import { Badge, Button } from "../../../design-v2";

/*
 * Presentation for one connected-account provider.
 *
 * Provider-agnostic on purpose: it receives an id, a display label, an
 * identifier, the connected flag, the two capability flags, the two callbacks
 * and a busy flag, and renders nothing it was not given. Google is the only
 * provider the server registry announces today; Apple would render through this
 * same component the day the canonical provider list announces it, with no
 * redesign and no second source of truth here.
 *
 * It owns no OAuth, no step-up and no network call — `ConnectedAccountsSection`
 * keeps all of that.
 */
export default function ConnectedAccountRow({
  providerId,
  label,
  identifier = null,
  connected = false,
  busy = false,
  actionsVisible = true,
  canConnect = false,
  canDisconnect = false,
  onConnect,
  onDisconnect,
  secondaryAction = null,
  note = null,
}) {
  // Connected without an address is a legacy identity: the row states the
  // connection rather than inventing an identifier for it.
  const identifierText = connected ? identifier : "Not connected";
  const showConnect = !connected && canConnect;
  const showDisconnect = connected;
  const hasActions =
    actionsVisible && (showConnect || showDisconnect || secondaryAction);

  return (
    <div className="lz-settings-account-row" data-provider-id={providerId}>
      <span className="lz-settings-account-row__mark" aria-hidden="true">
        <ProviderMark providerId={providerId} />
      </span>

      <p className="lz-settings-account-row__name">{label}</p>

      {/* The state is carried by the pill's text, never by its colour alone. */}
      {connected ? (
        <Badge tone="action" className="lz-settings-account-row__status">
          Connected
        </Badge>
      ) : null}

      {/* The identifier takes the row's full remaining width so an ordinary
          address is not squeezed against the state pill. */}
      {identifierText ? (
        <p className="lz-settings-account-row__identifier">{identifierText}</p>
      ) : null}

      {hasActions ? (
        <div className="lz-settings-account-row__actions">
          {showConnect ? (
            <Button
              type="button"
              variant="secondary"
              className="lz-settings-inline-action"
              disabled={busy}
              onClick={onConnect}
            >
              {busy ? "Connecting..." : `Connect ${label}`}
            </Button>
          ) : null}
          {showDisconnect ? (
            <Button
              type="button"
              variant="secondary"
              className="lz-settings-inline-action"
              disabled={busy || !canDisconnect}
              onClick={onDisconnect}
            >
              {busy ? "Disconnecting..." : "Unlink"}
            </Button>
          ) : null}
          {secondaryAction}
        </div>
      ) : null}

      {note ? <p className="lz-settings-account-row__note">{note}</p> : null}
    </div>
  );
}
